import axios from "axios";
import { API_BASE_URL } from "../config";

const workspaceClient = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  withCredentials: true,
});

export const getWorkspaceBoards = async () => (await workspaceClient.get("/workspace/boards")).data?.boards || [];
export const createWorkspaceBoard = async (payload) => (await workspaceClient.post("/workspace/boards", payload)).data?.board;
export const updateWorkspaceBoard = async (id, payload) => (await workspaceClient.patch(`/workspace/boards/${id}`, payload)).data?.board;
export const deleteWorkspaceBoard = async (id) => (await workspaceClient.delete(`/workspace/boards/${id}`)).data?.board;

const reservedWorkspacePathSegments = new Set([
  "admin",
  "assets",
  "auth",
  "callback",
  "documentation",
  "favicon.ico",
  "login",
  "manifest.json",
  "robots.txt",
  "static",
  "workspace",
]);

export const getWorkspaceSlugFromPath = () => {
  if (typeof window === "undefined") return "";
  const slug = window.location.pathname.split("/").filter(Boolean)[0] || "";
  if (reservedWorkspacePathSegments.has(slug.toLowerCase())) return "";
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/i.test(slug) ? slug.toLowerCase() : "";
};

const workspaceSsoStorageKey = "lyriclious-workspace-sso";
const workspaceSsoReturnStorageKey = "lyriclious-workspace-sso-return";
const workspaceLoginIntentParam = "workspace-login";
const workspaceLastSlugStorageKey = "lyriclious-workspace-last-slug";
let workspaceSsoMemoryToken = "";

const getSessionStorageValue = (key) => {
  try {
    return window.sessionStorage.getItem(key) || "";
  } catch (error) {
    return "";
  }
};

const setSessionStorageValue = (key, value) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (error) {
    // The in-memory handoff remains available if browser storage is unavailable.
  }
};

const removeSessionStorageValue = (key) => {
  try {
    window.sessionStorage.removeItem(key);
  } catch (error) {
    // No action is required when browser storage is unavailable.
  }
};

export const consumeWorkspaceSsoHandoff = () => {
  if (typeof window === "undefined") return "";

  try {
    const currentUrl = new URL(window.location.href);
    const hash = new URLSearchParams(currentUrl.hash.replace(/^#/, ""));
    const handoffToken = hash.get("workspace-sso") || currentUrl.searchParams.get("workspace-sso") || "";
    const hasWorkspaceLoginIntent = currentUrl.searchParams.get(workspaceLoginIntentParam) === "1";

    if (!handoffToken) {
      if (hasWorkspaceLoginIntent) setSessionStorageValue(workspaceSsoReturnStorageKey, "1");
      return "";
    }

    workspaceSsoMemoryToken = handoffToken;
    setSessionStorageValue(workspaceSsoStorageKey, handoffToken);
    setSessionStorageValue(workspaceSsoReturnStorageKey, "1");
    hash.delete("workspace-sso");
    currentUrl.searchParams.delete("workspace-sso");
    currentUrl.hash = hash.toString();
    window.history.replaceState({}, "", `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
    return handoffToken;
  } catch (error) {
    return "";
  }
};

export const isWorkspaceSsoReturn = () => {
  if (typeof window === "undefined") return false;
  consumeWorkspaceSsoHandoff();
  const currentUrl = new URL(window.location.href);
  return Boolean(
    workspaceSsoMemoryToken ||
    getSessionStorageValue(workspaceSsoReturnStorageKey) ||
    currentUrl.searchParams.get(workspaceLoginIntentParam) === "1"
  );
};

export const clearWorkspaceSsoReturn = () => {
  removeSessionStorageValue(workspaceSsoReturnStorageKey);
  if (typeof window === "undefined") return;

  const currentUrl = new URL(window.location.href);
  if (currentUrl.searchParams.get(workspaceLoginIntentParam) !== "1") return;
  currentUrl.searchParams.delete(workspaceLoginIntentParam);
  window.history.replaceState({}, "", `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
};

export const getRememberedWorkspaceSlug = () => {
  if (typeof window === "undefined") return "";
  try {
    const slug = window.localStorage.getItem(workspaceLastSlugStorageKey) || "";
    return /^[a-z0-9]+(?:-[a-z0-9]+)*$/i.test(slug) ? slug.toLowerCase() : "";
  } catch (error) {
    return "";
  }
};

export const rememberWorkspaceSlug = (slug) => {
  if (typeof window === "undefined") return;
  const normalizedSlug = `${slug || ""}`.trim().toLowerCase();
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/i.test(normalizedSlug)) return;
  try {
    window.localStorage.setItem(workspaceLastSlugStorageKey, normalizedSlug);
  } catch (error) {
    // Ignore storage failures.
  }
};

export const forgetRememberedWorkspaceSlug = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(workspaceLastSlugStorageKey);
  } catch (error) {
    // The redirect remains correct even when browser storage is unavailable.
  }
};

export const logoutWorkspaceSession = async () => {
  // The central Lyriclious session cookie is shared by the local applications.
  // Clearing it here signs the user out of both Workspace and Lyriclious.
  await workspaceClient.get("/logout");
  workspaceSsoMemoryToken = "";
  removeSessionStorageValue(workspaceSsoStorageKey);
  removeSessionStorageValue(workspaceSsoReturnStorageKey);
  removeSessionStorageValue("lyriclious-workspace-login-return");
  forgetRememberedWorkspaceSlug();
};

const getWorkspaceSsoToken = () => {
  if (typeof window === "undefined") return "";
  return consumeWorkspaceSsoHandoff() || workspaceSsoMemoryToken || getSessionStorageValue(workspaceSsoStorageKey);
};

if (typeof window !== "undefined") consumeWorkspaceSsoHandoff();

workspaceClient.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  const slug = getWorkspaceSlugFromPath();
  if (slug) config.headers["x-workspace-slug"] = slug;
  const token = getWorkspaceSsoToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const getWorkspaceBootstrap = async () => {
  const { data } = await workspaceClient.get("/workspace/bootstrap");
  return data;
};

export const getWorkspaceHomeDashboard = async (scope = "personal") => {
  const { data } = await workspaceClient.get("/workspace/home-dashboard", { params: { scope } });
  return data;
};

export const captureWorkspaceOnboardingLead = async (payload = {}) => {
  const { data } = await workspaceClient.post("/workspace/onboarding-lead", payload);
  return data;
};

export const getWorkspaceCalendarConnections = async () => {
  const { data } = await workspaceClient.get("/workspace/calendar/connections");
  return data;
};

export const startWorkspaceCalendarConnection = async (provider) => {
  const { data } = await workspaceClient.post(`/workspace/calendar/oauth/${provider}`);
  return data;
};

export const disconnectWorkspaceCalendar = async (provider) => {
  const { data } = await workspaceClient.delete(`/workspace/calendar/connections/${provider}`);
  return data;
};

export const getWorkspaceExternalCalendarEvents = async ({ start, end }) => {
  const { data } = await workspaceClient.get("/workspace/calendar/external-events", { params: { start, end } });
  return data;
};

export const requestWorkspaceInviteRenewal = async () => {
  const { data } = await workspaceClient.post("/workspace/invite-renewal-request");
  return data;
};

export const submitWorkspaceEnquiry = async (payload) => {
  const { data } = await workspaceClient.post("/workspace-enquiry-leads", payload);
  return data;
};

export const getWorkspaceClientLogos = async (params = {}) => {
  const { data } = await workspaceClient.get("/testimonials", {
    params: {
      audienceType: "COMPANY",
      successStory: false,
      logoOnly: true,
      limit: 24,
      ...params,
    },
  });
  return data;
};

export const getWorkspaceAdministration = async () => {
  const { data } = await workspaceClient.get("/workspace/administration");
  return data;
};

export const updateWorkspaceAdministration = async (payload) => {
  const { data } = await workspaceClient.patch("/workspace/administration", payload);
  return data;
};
export const getWorkspaceOwnershipCandidates = async () => (await workspaceClient.get("/workspace/administration/ownership-candidates")).data;
export const transferWorkspaceOwnership = async (payload) => (await workspaceClient.post("/workspace/administration/transfer-ownership", payload)).data;

export const updateWorkspaceAccountPreferences = async (payload) => {
  const { data } = await workspaceClient.put("/account/preferences", payload);
  return data;
};

export const exportWorkspaceAccountData = async () => {
  const response = await workspaceClient.get("/account/export", {
    responseType: "blob",
  });
  return response.data;
};

export const getWorkspacePeople = async (project, projectId = "") => {
  const { data } = await workspaceClient.get("/workspace/people", {
    params: project || projectId ? { ...(project ? { project } : {}), ...(projectId ? { projectId } : {}) } : undefined,
  });
  return data;
};

export const getWorkspaceTraining = async (kind = "") => (await workspaceClient.get("/workspace/training", { params: kind ? { kind } : undefined })).data;
export const createWorkspaceTraining = async (payload) => (await workspaceClient.post("/workspace/training", payload)).data;
export const updateWorkspaceTraining = async (id, payload) => (await workspaceClient.patch(`/workspace/training/${id}`, payload)).data;

export const searchWorkspacePeopleUsers = async (query) => {
  const { data } = await workspaceClient.get("/workspace/people/search-users", {
    params: { q: query },
  });
  return data;
};

export const createWorkspacePerson = async (payload) => {
  const { data } = await workspaceClient.post("/workspace/people", payload);
  return data;
};

export const createWorkspaceDepartment = async (payload) => {
  const { data } = await workspaceClient.post("/workspace/people/departments", payload);
  return data;
};

export const updateWorkspaceDepartment = async (id, payload) => {
  const { data } = await workspaceClient.patch(`/workspace/people/departments/${id}`, payload);
  return data;
};

export const createWorkspaceTeam = async (payload) => {
  const { data } = await workspaceClient.post("/workspace/people/teams", payload);
  return data;
};

export const updateWorkspaceTeam = async (id, payload) => {
  const { data } = await workspaceClient.patch(`/workspace/people/teams/${id}`, payload);
  return data;
};

export const createWorkspaceRoleTemplate = async (payload) => {
  const { data } = await workspaceClient.post("/workspace/people/roles", payload);
  return data;
};

export const updateWorkspaceRoleTemplate = async (id, payload) => {
  const { data } = await workspaceClient.patch(`/workspace/people/roles/${id}`, payload);
  return data;
};
export const cloneWorkspaceRoleTemplate = async (id, payload) => (await workspaceClient.post(`/workspace/people/roles/${id}/clone`, payload)).data;
export const archiveWorkspaceRoleTemplate = async (id) => (await workspaceClient.post(`/workspace/people/roles/${id}/archive`)).data;
export const updateWorkspacePersonOverrides = async (id, payload) => (await workspaceClient.put(`/workspace/people/${id}/overrides`, payload)).data;
export const getWorkspaceAccessRequests = async () => (await workspaceClient.get("/workspace/people/access-requests")).data;
export const createWorkspaceAccessRequest = async (payload) => (await workspaceClient.post("/workspace/people/access-requests", payload)).data;
export const decideWorkspaceAccessRequest = async (id, payload) => (await workspaceClient.patch(`/workspace/people/access-requests/${id}`, payload)).data;
export const getWorkspaceAttendance = async () => (await workspaceClient.get("/workspace/people/attendance")).data;
export const clockWorkspaceAttendance = async (action) => (await workspaceClient.post("/workspace/people/attendance", { action })).data;
export const heartbeatWorkspaceAttendance = async () => (await workspaceClient.post("/workspace/people/attendance/heartbeat")).data;
export const updateWorkspaceAttendanceSettings = async (payload) => (await workspaceClient.patch("/workspace/people/attendance/settings", payload)).data;
export const getWorkspaceOrganization = async () => (await workspaceClient.get("/workspace/people/organization")).data;
export const createWorkspaceOrganizationNode = async (payload) => (await workspaceClient.post("/workspace/people/organization", payload)).data;
export const updateWorkspaceOrganizationNode = async (id, payload) => (await workspaceClient.patch(`/workspace/people/organization/nodes/${id}`, payload)).data;
export const createWorkspaceOrganizationRelationship = async (payload) => (await workspaceClient.post("/workspace/people/organization/relationships", payload)).data;

export const updateWorkspacePerson = async (id, payload) => {
  if (!/^[a-f\d]{24}$/i.test(`${id || ""}`)) {
    throw new Error("This person is not yet a saved workspace profile. Refresh People and try again.");
  }
  const { data } = await workspaceClient.patch(`/workspace/people/${id}`, payload);
  return data;
};

export const resendWorkspacePersonInvite = async (id) => {
  const { data } = await workspaceClient.post(`/workspace/people/${id}/invite`);
  return data;
};

export const createWorkspaceRecord = async (payload) => {
  const { data } = await workspaceClient.post("/workspace/records", payload);
  return data;
};

export const submitWorkspaceReport = async (payload) => {
  const { data } = await workspaceClient.post("/workspace/report", payload);
  return data;
};

export const updateWorkspaceRecord = async (id, payload) => {
  const { data } = await workspaceClient.patch(`/workspace/records/${id}`, payload);
  return data;
};

export const deleteWorkspaceRecord = async (id) => {
  const { data } = await workspaceClient.delete(`/workspace/records/${id}`);
  return data;
};

export const getWorkspaceRecordTimeline = async (id) => {
  const { data } = await workspaceClient.get(`/workspace/records/${id}/timeline`);
  return data;
};

export const addWorkspaceRecordComment = async (id, payload) => {
  const { data } = await workspaceClient.post(`/workspace/records/${id}/comments`, payload);
  return data;
};

export const updateWorkspaceRecordComment = async (recordId, commentId, payload) => {
  const { data } = await workspaceClient.patch(`/workspace/records/${recordId}/comments/${commentId}`, payload);
  return data;
};

export const deleteWorkspaceRecordComment = async (recordId, commentId) => {
  const { data } = await workspaceClient.delete(`/workspace/records/${recordId}/comments/${commentId}`);
  return data;
};

export const toggleWorkspaceRecordCommentLike = async (recordId, commentId) => {
  const { data } = await workspaceClient.post(`/workspace/records/${recordId}/comments/${commentId}/like`);
  return data;
};

export const toggleWorkspaceRecordCommentReaction = async (recordId, commentId, reactionKey) => {
  const { data } = await workspaceClient.post(`/workspace/records/${recordId}/comments/${commentId}/reactions`, { reactionKey });
  return data;
};

export const pingWorkspaceRecordPresence = async (id, payload = {}) => {
  const { data } = await workspaceClient.post(`/workspace/records/${id}/presence`, payload);
  return data;
};

export const startWorkspaceRecordViewSession = async (id) => {
  const { data } = await workspaceClient.post(`/workspace/records/${id}/view-session`);
  return data;
};

export const endWorkspaceRecordViewSession = async (id, sessionId) => {
  const { data } = await workspaceClient.patch(`/workspace/records/${id}/view-session`, { session_id: sessionId });
  return data;
};

export const trackWorkspacePageVisit = async (payload) => (await workspaceClient.post("/workspace/page-visits", payload)).data;
